import { routeObservabilityTags } from '../observability/metrics';
import type { PublicTelemetry } from '../telemetry/events';
import { hasSensitiveHash } from '../security/redaction';
import { matchPublicRoute } from './route-registry';
import type { PublicRouteMatch } from './types';

export type PublicRouterDecisionAction = 'browser-navigation' | 'client-enhancement';

export type PublicRouterDecisionReason =
  | 'external-origin'
  | 'modified-click'
  | 'non-primary-button'
  | 'download-link'
  | 'targeted-link'
  | 'unknown-route'
  | 'static-document'
  | 'sensitive-hash'
  | 'router-disabled'
  | 'no-transition-handler'
  | 'eligible-enhancement';

export type PublicRouterDecision = {
  action: PublicRouterDecisionAction;
  reason: PublicRouterDecisionReason;
  route: PublicRouteMatch;
  canonicalPath: string;
  sourceOfTruth: 'server-edge-route';
  directlyOpenable: true;
  clientRouterMayBeSourceOfTruth: false;
  sensitiveHashPresent: boolean;
};

export type PublicNavigationInput = {
  currentUrl: string | URL;
  targetUrl: string | URL;
  routerEnabled?: boolean;
  hasTransitionHandler?: boolean;
  button?: number;
  modifiedClick?: boolean;
  download?: boolean;
  target?: string | null;
};

export type PublicRouterOptions = {
  doc: Document;
  win: Window;
  telemetry: PublicTelemetry;
  enabled: boolean;
  hasTransitionHandler?: boolean;
  onNavigate?: (route: PublicRouteMatch) => void | Promise<void>;
};

export type PublicRouter = {
  start(): void;
  destroy(): void;
};

export function createPublicRouter(options: PublicRouterOptions): PublicRouter {
  let started = false;

  const onClick = (event: MouseEvent) => {
    const anchor = findAnchor(event.target);
    if (!anchor) return;

    const currentUrl =
      options.win.location.origin + options.win.location.pathname + options.win.location.search;
    const decision = decidePublicNavigation({
      currentUrl,
      targetUrl: anchor.href,
      routerEnabled: options.enabled,
      hasTransitionHandler: options.hasTransitionHandler === true,
      button: event.button,
      modifiedClick: hasModifiedClick(event),
      download: anchor.hasAttribute('download'),
      target: anchor.getAttribute('target'),
    });

    emitNavigationDecision(options.telemetry, decision);

    if (decision.action !== 'client-enhancement') return;

    event.preventDefault();
    options.win.history.pushState(
      { quaomPublicRoute: decision.route.canonicalPath },
      '',
      decision.route.canonicalPath,
    );
    void options.onNavigate?.(decision.route);
  };

  const onPopState = () => {
    const route = matchPublicRoute(options.win.location.pathname, options.win.location.hash);
    options.telemetry.emit({
      name: 'public_router_transition_fallback',
      pageId: route.id,
      route: route.canonicalPath,
      ok: true,
      context: {
        ...routeObservabilityTags(route),
        sourceOfTruth: 'server-edge-route',
        clientRouterMayBeSourceOfTruth: false,
      },
    });
    void options.onNavigate?.(route);
  };

  return {
    start() {
      if (started) return;
      started = true;
      options.doc.addEventListener('click', onClick, { capture: true });
      options.win.addEventListener('popstate', onPopState);
    },
    destroy() {
      if (!started) return;
      started = false;
      options.doc.removeEventListener('click', onClick, { capture: true });
      options.win.removeEventListener('popstate', onPopState);
    },
  };
}

export function decidePublicNavigation(input: PublicNavigationInput): PublicRouterDecision {
  const currentUrl = toUrl(input.currentUrl, 'https://quaom.invalid/');
  const targetUrl = toUrl(input.targetUrl, currentUrl);
  const route = matchPublicRoute(targetUrl.pathname, targetUrl.hash);
  const sensitiveHashPresent = hasSensitiveHash(targetUrl.hash);

  if (input.button !== undefined && input.button !== 0)
    return browserDecision('non-primary-button', route, sensitiveHashPresent);
  if (input.modifiedClick) return browserDecision('modified-click', route, sensitiveHashPresent);
  if (input.download) return browserDecision('download-link', route, sensitiveHashPresent);
  if (input.target && input.target.toLowerCase() !== '_self')
    return browserDecision('targeted-link', route, sensitiveHashPresent);
  if (targetUrl.origin !== currentUrl.origin)
    return browserDecision('external-origin', route, sensitiveHashPresent);
  if (route.kind === 'unknown')
    return browserDecision('unknown-route', route, sensitiveHashPresent);
  if (route.kind === 'static')
    return browserDecision('static-document', route, sensitiveHashPresent);
  if (sensitiveHashPresent) return browserDecision('sensitive-hash', route, sensitiveHashPresent);
  if (input.routerEnabled !== true)
    return browserDecision('router-disabled', route, sensitiveHashPresent);
  if (input.hasTransitionHandler !== true)
    return browserDecision('no-transition-handler', route, sensitiveHashPresent);

  return {
    action: 'client-enhancement',
    reason: 'eligible-enhancement',
    route,
    canonicalPath: route.canonicalPath,
    sourceOfTruth: 'server-edge-route',
    directlyOpenable: true,
    clientRouterMayBeSourceOfTruth: false,
    sensitiveHashPresent,
  };
}

function browserDecision(
  reason: PublicRouterDecisionReason,
  route: PublicRouteMatch,
  sensitiveHashPresent: boolean,
): PublicRouterDecision {
  return {
    action: 'browser-navigation',
    reason,
    route,
    canonicalPath: route.canonicalPath,
    sourceOfTruth: 'server-edge-route',
    directlyOpenable: true,
    clientRouterMayBeSourceOfTruth: false,
    sensitiveHashPresent,
  };
}

function findAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest('a[href]');
}

function hasModifiedClick(event: MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function emitNavigationDecision(telemetry: PublicTelemetry, decision: PublicRouterDecision): void {
  telemetry.emit({
    name: 'public_router_transition_attempted',
    pageId: decision.route.id,
    route: decision.canonicalPath,
    ok: true,
    context: {
      ...routeObservabilityTags(decision.route),
      action: decision.action,
      reason: decision.reason,
      sourceOfTruth: decision.sourceOfTruth,
      clientRouterMayBeSourceOfTruth: decision.clientRouterMayBeSourceOfTruth,
      directlyOpenable: decision.directlyOpenable,
      sensitiveHashPresent: decision.sensitiveHashPresent,
    },
  });

  telemetry.emit({
    name:
      decision.action === 'client-enhancement'
        ? 'public_router_transition_intercepted'
        : decision.reason === 'sensitive-hash'
          ? 'public_router_transition_blocked'
          : 'public_router_transition_fallback',
    pageId: decision.route.id,
    route: decision.canonicalPath,
    ok: decision.action === 'client-enhancement',
    context: {
      ...routeObservabilityTags(decision.route),
      reason: decision.reason,
      sensitiveHashPresent: decision.sensitiveHashPresent,
    },
  });
}

function toUrl(url: string | URL, base: string | URL): URL {
  return url instanceof URL ? url : new URL(String(url), base);
}
